/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Expose the upload, review-step and zoom entry points to the shell.
 * @state-owner activities UI store owns the upload draft and question selection
 * @allowed-imports React; same-module store and components; dataset and workspace stores; shared constants
 * @forbidden component markup outside the module; direct API client
 * @public-api via modules/activities/index.ts
 * @side-effects opens the upload dialog and scrolls the reviewed question into view
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useCallback } from 'react';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { QUESTIONS } from '../../../shared/lib/demo-data';
import { findActivity } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { UploadDialog } from '../components/UploadDialog';
import { useActivityUiStore } from '../store/activity-ui-store';

/** Scope the draft to a class, then open the dialog. */
export function useUploadDialog(): (classIndex?: number | null) => void {
  const { openModal } = useOverlay();
  const startUpload = useActivityUiStore((state) => state.startUpload);
  return useCallback(
    (classIndex: number | null = null) => {
      startUpload(classIndex);
      openModal(<UploadDialog />);
    },
    [openModal, startUpload],
  );
}

/** Jump to the next or previous question awaiting review. */
export function useReviewStepper(): (direction: number) => void {
  const notify = useWorkspaceUiStore((state) => state.notify);
  return useCallback(
    (direction: number) => {
      const navigation = useWorkspaceUiStore.getState();
      const dataset = useDatasetStore.getState();
      const activityStore = useActivityUiStore.getState();
      const activity = findActivity(dataset.activities, navigation.selection);
      if (!activity) return;
      const total = QUESTIONS.length;
      for (let step = 1; step <= total; step += 1) {
        const index =
          (((activityStore.selectedQuestion + direction * step) % total) + total) % total;
        if (!activity.reviewed[index]) {
          activityStore.setSelectedQuestion(index);
          window.setTimeout(() => {
            document
              .getElementById(`q${index}`)
              ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }, 30);
          return;
        }
      }
      notify('沒有待核准的題目');
    },
    [notify],
  );
}

/** The paper zoom the `⌘ +` / `⌘ -` shortcuts drive. */
export function usePaperZoom(): (delta: number) => void {
  const changeZoom = useActivityUiStore((state) => state.changeZoom);
  return useCallback((delta: number) => changeZoom(delta), [changeZoom]);
}

/** True when the shell may offer review stepping for the current selection. */
export function useCanStepReview(): boolean {
  const section = useWorkspaceUiStore((state) => state.section);
  const selection = useWorkspaceUiStore((state) => state.selection);
  const activities = useDatasetStore((state) => state.activities);
  return section === 'act' && Boolean(findActivity(activities, selection));
}
